import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArticleEditor from '../../../../components/dashboardcomponents/KnowledgeBase/NewArticle/ArticleEditor';
import ArticleSideBar from '../../../../components/dashboardcomponents/KnowledgeBase/NewArticle/ArticleSideBar';
import KbTagPopup from '../../../../components/dashboardcomponents/KnowledgeBase/NewArticle/KbTagPopup';
import { createKbArticle, resetCreateKbArticleState } from '../../../../store/features/createKbArticle';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getKbGroupId, setKbGroupId } from '../../../../utils/kbGroupId';

const NewArticle = () => {
  const dispatch = useDispatch();
  const { loading, success, error } = useSelector((state) => state.createKbArticle);
  const business_id = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const kbRole = useSelector((state) => state.loginToken?.response?.user?.kbRole?.role);
  const navigate = useNavigate();
  const location = useLocation();
  const groupId = location.state?.groupId || getKbGroupId();

  // Viewers should not be able to create/edit articles
  const isReadOnly = kbRole === 'viewer';

  // State for the article data
  const [articleData, setArticleData] = useState({
    title: "",
    content: "<p></p>",
    mode: "edit",
    lastDateEdit: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
    lastEditBy: "Current User",
    author: "Current User",
    categoryIds: [],
    visibility: "public",
    tags: [],
    comments: [],
    disableComments: false
  });

  const [showTagPopup, setShowTagPopup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Handle success/error after save
  useEffect(() => {
    if (isSaving && success) {
      toast.success('Article created successfully!');
      dispatch(resetCreateKbArticleState());
      setIsSaving(false);
      if (groupId) setKbGroupId(groupId);
      navigate('/kb/articles');
    }
    if (isSaving && error) {
      toast.error(error?.message || 'Failed to create article');
      dispatch(resetCreateKbArticleState());
      setIsSaving(false);
    }
  }, [success, error, isSaving, dispatch, navigate, groupId]);

  // Event listener for DashboardHeader save action
  useEffect(() => {
    const handleSaveRequest = (event) => {
      const actionType = event.detail;
      if (actionType) {
        handleSaveAction(actionType);
      }
    };

    window.addEventListener('SAVE_ARTICLE_TRIGGER', handleSaveRequest);

    return () => {
      window.removeEventListener('SAVE_ARTICLE_TRIGGER', handleSaveRequest);
    };
  }, [articleData]);

  const setTitle = (newTitle) => {
    setArticleData(prev => ({ ...prev, title: newTitle }));
  };

  // Handler for content change from Editor
  const setContent = (newContent) => {
    setArticleData(prev => ({ ...prev, content: newContent }));
  };

  const handleSaveAction = async (type) => {
    if (type === 'cancel') {
      navigate('/kb/articles');
      return;
    }

    if (!articleData.title.trim()) {
      toast.error('Please enter a title for the article');
      return;
    }

    if (articleData.title.trim().length < 3) {
      toast.error('Title must be at least 3 characters');
      return;
    }

    if (articleData.title.length > 50) {
      toast.error('Title cannot exceed 50 characters');
      return;
    }

    // Validate Content Body
    const isContentEmpty = !articleData.content ||
      articleData.content.trim() === '' ||
      articleData.content === '<p></p>' ||
      articleData.content === '<p><br></p>';

    if (isContentEmpty) {
      toast.error('Article content is required');
      return;
    }

    // if (!articleData.categoryIds || articleData.categoryIds.length === 0) {
    //   toast.error('At least one category is required');
    //   return;
    // }

    if (groupId) setKbGroupId(groupId);

    const payload = {
      business_id: business_id,
      group_id: groupId,
      title: articleData.title,
      content: articleData.content,
      categoryIds: (articleData.categoryIds || [])
        .map((c) => (c && typeof c === "object" ? c._id || c.id : c))
        .filter(Boolean),
      saveAction: type,
      visibility: articleData.visibility,
      tagIds: articleData.tags.map(t => t._id || t.id).filter(id => id),
      comments: articleData.comments,
      disableComments: articleData.disableComments
    };

    setIsSaving(true);
    dispatch(createKbArticle(payload));
  };

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%', backgroundColor: '#fff', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}>
        <ArticleEditor
          title={articleData.title}
          setTitle={setTitle}
          content={articleData.content}
          setContent={setContent}
          isReadOnly={isReadOnly}
        />
      </div>
      <div style={{ width: '420px', flexShrink: 0, borderLeft: '1px solid #e5e7eb', height: '100%', overflowY: 'auto' }}>
        <ArticleSideBar
          articleData={articleData}
          setArticleData={setArticleData}
          onAddTag={() => setShowTagPopup(true)}
          isReadOnly={isReadOnly}
          groupId={groupId}
        />
      </div>
      {showTagPopup && (
        <KbTagPopup
          onClose={() => setShowTagPopup(false)}
          onApply={(newTags) => {
            setArticleData(prev => ({ ...prev, tags: newTags }));
          }}
          selectedTags={articleData.tags}
        />
      )}
    </div>
  );
};

export default NewArticle;
